import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { PropertiesIcon } from './MobileNav';
import { MapPin, ChevronDown, Users, User as UserIcon, Mail, Clock, Check } from 'lucide-react';

const ACCENTS = {
  emerald: { icon: 'text-emerald-500', hover: 'hover:border-emerald-300 hover:bg-emerald-50/40', badge: 'from-emerald-500 to-blue-500', iconBg: 'bg-emerald-50' },
  purple: { icon: 'text-purple-500', hover: 'hover:border-purple-300 hover:bg-purple-50/40', badge: 'from-purple-500 to-indigo-500', iconBg: 'bg-purple-50' },
};

export function AssignedPropertiesDropdown({ properties = [], testid, accent = 'emerald' }) {
  const A = ACCENTS[accent] || ACCENTS.emerald;
  const count = properties.length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`inline-flex items-center gap-2 h-10 px-3.5 rounded-xl border border-slate-200 bg-white ${A.hover} text-sm font-semibold text-slate-700 transition-colors w-full justify-between`}
        >
          <span className="flex items-center gap-2 min-w-0">
            <PropertiesIcon size={15} className={`${A.icon} flex-shrink-0`} strokeWidth={1.9} />
            <span className="truncate">Properties assigned</span>
          </span>
          <span className="flex items-center gap-1.5 flex-shrink-0">
            <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-gradient-to-r ${A.badge} text-white text-xs font-bold`}>
              {count}
            </span>
            <ChevronDown size={15} className="text-slate-400" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-0 rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-xl"
        data-testid={testid ? `${testid}-content` : undefined}
      >
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
          <h4 className="text-sm font-bold text-slate-900">Assigned properties</h4>
          <p className="text-xs text-slate-400">{count} propert{count === 1 ? 'y' : 'ies'}</p>
        </div>
        {count === 0 ? (
          <div className="px-4 py-6 text-center">
            <PropertiesIcon size={28} className="mx-auto text-slate-300 mb-2" strokeWidth={1.5} />
            <p className="text-sm text-slate-400">No properties assigned yet</p>
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto py-1">
            {properties.map((p) => (
              <div key={p.id} className="flex items-start gap-2.5 px-4 py-2.5 hover:bg-slate-50 transition-colors">
                <div className={`w-8 h-8 rounded-lg ${A.iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <PropertiesIcon size={16} className={A.icon} strokeWidth={1.9} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{p.name}</p>
                  {p.address ? (
                    <p className="text-xs text-slate-500 flex items-start gap-1 mt-0.5">
                      <MapPin size={11} className="text-slate-400 flex-shrink-0 mt-0.5" />
                      <span className="leading-snug">{p.address}</span>
                    </p>
                  ) : (
                    <p className="text-xs text-slate-400 italic mt-0.5">No address</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function CleanersDropdown({ cleaners = [], pending = [], testid }) {
  const count = cleaners.length;
  const isEmpty = count === 0 && pending.length === 0;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="inline-flex items-center gap-2 h-10 px-3.5 rounded-xl border border-slate-200 bg-white hover:border-purple-300 hover:bg-purple-50/40 text-sm font-semibold text-slate-700 transition-colors w-full justify-between"
        >
          <span className="flex items-center gap-2 min-w-0">
            <Users size={15} className="text-purple-500 flex-shrink-0" strokeWidth={1.9} />
            <span className="truncate">Cleaners</span>
          </span>
          <span className="flex items-center gap-1.5 flex-shrink-0">
            <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs font-bold">
              {count}
            </span>
            <ChevronDown size={15} className="text-slate-400" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="top"
        sideOffset={8}
        collisionPadding={12}
        className="w-72 p-0 rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-xl"
        data-testid={testid ? `${testid}-content` : undefined}
      >
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
          <h4 className="text-sm font-bold text-slate-900">Cleaners</h4>
          <p className="text-xs text-slate-400">{count} active{pending.length ? ` · ${pending.length} pending` : ''}</p>
        </div>
        {isEmpty ? (
          <div className="px-4 py-6 text-center">
            <Users size={28} className="mx-auto text-slate-300 mb-2" strokeWidth={1.5} />
            <p className="text-sm text-slate-400">No cleaners yet</p>
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto py-1">
            {cleaners.map((cl) => (
              <div key={cl.id} className="flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-slate-50 transition-colors" data-testid={`cleaner-row-${cl.id}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                    <UserIcon size={16} className="text-purple-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{cl.name || cl.email}</p>
                    <p className="text-xs text-slate-500">{(cl.assignedProperties || []).length} propert{(cl.assignedProperties || []).length === 1 ? 'y' : 'ies'}</p>
                  </div>
                </div>
                <span className="badge-3d bg-green-50 text-green-700 border-green-200 text-xs flex-shrink-0"><Check size={11} /> Active</span>
              </div>
            ))}
            {pending.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-slate-50 transition-colors" data-testid={`pending-cleaner-${inv.id}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <Mail size={15} className="text-amber-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{inv.name}</p>
                    <p className="text-xs text-slate-500 truncate">{inv.email}</p>
                  </div>
                </div>
                <span className="badge-3d badge-warning text-xs flex-shrink-0"><Clock size={11} /> Pending</span>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
